import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, Pencil, Trash2, Plus } from 'lucide-react';
import {
  AcademicClass,
  AcademicClassesApi,
  ClassSubjectRow,
  MediumsApi,
  SectionsApi,
} from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<AcademicClass>();

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

function ClassSubjectsTable({ rows }: { rows: ClassSubjectRow[] }) {
  if (!rows.length) {
    return <p className="text-sm text-slate-500">No subjects assigned to this class yet.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
          <th className="pb-2">Subject</th>
          <th className="pb-2">Section</th>
          <th className="pb-2">Teacher</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <tr key={`${r.subjectId}-${r.sectionId ?? i}`}>
            <td className="py-2 pr-2 font-medium text-slate-900">{r.subjectName}</td>
            <td className="py-2 pr-2">
              {r.sectionName ? (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                  {r.sectionName}
                </span>
              ) : (
                '—'
              )}
            </td>
            <td className="py-2 text-slate-700">
              {/* Rendered as-is, including known-doubled legacy names — see
                  docs/data-issues.md, not a bug to hide here. */}
              {r.teacherName ?? <span className="text-slate-400">No teacher assigned</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SectionChips({ sections }: { sections: { id: string; name: string }[] }) {
  if (!sections.length) {
    return <span className="text-xs text-slate-400">No sections</span>;
  }
  return (
    <div className="flex max-w-xs flex-wrap gap-1">
      {sections.map((s) => (
        <span
          key={s.id}
          className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
        >
          {s.name}
        </span>
      ))}
    </div>
  );
}

/** Add/rename/delete sections for one class, shown inside the class detail modal. */
function SectionManager({ classId, sections }: { classId: string; sections: { id: string; name: string }[] }) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const [deleteError, setDeleteError] = useState<string | undefined>();

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['academic-classes'] });
  }

  const create = useMutation({
    mutationFn: () => SectionsApi.create({ name: newName.trim(), classId }),
    onSuccess: () => {
      setNewName('');
      invalidate();
    },
  });
  const update = useMutation({
    mutationFn: () => SectionsApi.update(editingId as string, editingName.trim()),
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: () => SectionsApi.remove(deleting!.id),
    onSuccess: () => {
      setDeleting(null);
      setDeleteError(undefined);
      invalidate();
    },
    onError: (e) => setDeleteError(errMsg(e)),
  });

  return (
    <div>
      {sections.length === 0 ? (
        <p className="mb-2 text-xs text-slate-400">No sections yet.</p>
      ) : (
        <ul className="mb-2 divide-y divide-slate-100">
          {sections.map((s) => (
            <li key={s.id} className="flex items-center justify-between py-1.5">
              {editingId === s.id ? (
                <div className="flex flex-1 items-center gap-2">
                  <Input
                    className="!py-1"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    autoFocus
                  />
                  <button
                    className="btn-primary !px-2 !py-1 text-xs"
                    disabled={!editingName.trim() || update.isPending}
                    onClick={() => update.mutate()}
                  >
                    Save
                  </button>
                  <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => setEditingId(null)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <span className="text-sm text-slate-700">{s.name}</span>
                  <span className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      title="Rename"
                      className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
                      onClick={() => {
                        setEditingId(s.id);
                        setEditingName(s.name);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      title="Delete"
                      className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
                      onClick={() => {
                        setDeleteError(undefined);
                        setDeleting(s);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <Input
          className="!py-1.5"
          placeholder="New section name (e.g. C)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <button
          className="btn-secondary flex items-center gap-1 !py-1.5 text-xs"
          disabled={!newName.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
      {create.error && <p className="mt-1 text-xs text-red-600">{errMsg(create.error)}</p>}

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
        title="Delete section?"
        message={
          deleteError ??
          `This will permanently remove section "${deleting?.name}". This cannot be undone.`
        }
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </div>
  );
}

export function ClassesPage() {
  const qc = useQueryClient();
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'name', defaultSortOrder: 'asc' });
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isFetching, error } = useQuery({
    queryKey: ['academic-classes', dt.params],
    queryFn: () =>
      AcademicClassesApi.list({
        ...dt.params,
        sortBy: dt.params.sortBy as 'name' | 'medium' | undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const { data: mediums } = useQuery({
    queryKey: ['mediums'],
    queryFn: MediumsApi.list,
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['academic-classes', detailId],
    queryFn: () => AcademicClassesApi.get(detailId as string),
    enabled: !!detailId,
  });

  const { data: classSubjects, isLoading: classSubjectsLoading } = useQuery({
    queryKey: ['academic-classes', detailId, 'subjects'],
    queryFn: () => AcademicClassesApi.subjects(detailId as string),
    enabled: !!detailId,
  });

  function invalidateList() {
    qc.invalidateQueries({ queryKey: ['academic-classes'] });
  }

  // ── Create Class ──
  const [form, setForm] = useState({ name: '', mediumId: '', sectionNamesInput: '' });
  const create = useMutation({
    mutationFn: () =>
      AcademicClassesApi.create({
        name: form.name.trim(),
        mediumId: form.mediumId,
        sectionNames: form.sectionNamesInput
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      setForm({ name: '', mediumId: '', sectionNamesInput: '' });
      invalidateList();
    },
  });

  // ── Edit Class ──
  const [editingClass, setEditingClass] = useState<AcademicClass | null>(null);
  const [editForm, setEditForm] = useState({ name: '', mediumId: '' });
  const update = useMutation({
    mutationFn: () => AcademicClassesApi.update(editingClass!.id, editForm),
    onSuccess: () => {
      setEditingClass(null);
      invalidateList();
    },
  });

  // ── Delete Class ──
  const [deletingClass, setDeletingClass] = useState<AcademicClass | null>(null);
  const [deleteClassError, setDeleteClassError] = useState<string | undefined>();
  const remove = useMutation({
    mutationFn: () => AcademicClassesApi.remove(deletingClass!.id),
    onSuccess: () => {
      setDeletingClass(null);
      setDeleteClassError(undefined);
      invalidateList();
    },
    onError: (e) => setDeleteClassError(errMsg(e)),
  });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('name', {
      header: 'Name',
      enableSorting: true,
      cell: (info) => (
        <button
          type="button"
          onClick={() => setDetailId(info.row.original.id)}
          className="font-medium text-brand-600 hover:underline"
        >
          {info.getValue()}
        </button>
      ),
    }),
    columnHelper.accessor('mediumName', {
      id: 'medium',
      header: 'Medium',
      enableSorting: true,
    }),
    columnHelper.accessor('sections', {
      header: 'Sections',
      enableSorting: false,
      cell: (info) => <SectionChips sections={info.getValue()} />,
    }),
    columnHelper.accessor('sectionCount', {
      header: '# Sections',
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: (info) => (
        <span className="inline-flex items-center gap-2">
          <button
            type="button"
            title="Edit"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
            onClick={() => {
              setEditingClass(info.row.original);
              setEditForm({ name: info.row.original.name, mediumId: info.row.original.mediumId });
            }}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Delete"
            className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
            onClick={() => {
              setDeleteClassError(undefined);
              setDeletingClass(info.row.original);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </span>
      ),
    }),
  ];

  return (
    <div>
      <PageHeader title="Classes & Sections" />

      {/* Create Class */}
      <div className="card mb-6 p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">Create Class</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Medium" required>
            <Select value={form.mediumId} onChange={(e) => setForm((f) => ({ ...f, mediumId: e.target.value }))}>
              <option value="">Select Medium</option>
              {(mediums ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
          <Field label="Section(s)" required hint="Comma-separated, e.g. A, B">
            <Input
              value={form.sectionNamesInput}
              onChange={(e) => setForm((f) => ({ ...f, sectionNamesInput: e.target.value }))}
              placeholder="A, B"
            />
          </Field>
        </div>
        <div className="mt-3">
          <button
            className="btn-primary"
            disabled={
              !form.name.trim() ||
              !form.mediumId ||
              !form.sectionNamesInput.split(',').map((s) => s.trim()).filter(Boolean).length ||
              create.isPending
            }
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Saving…' : 'Submit'}
          </button>
        </div>
        {create.isSuccess && (
          <div className="mt-3 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">✓ Class created.</div>
        )}
        {create.error && (
          <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(create.error)}</div>
        )}
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {errMsg(error)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          result={data}
          state={dt}
          loading={isFetching}
          emptyMessage={
            dt.params.search
              ? 'No classes match your search.'
              : 'No classes set up for this school yet.'
          }
          toolbar={
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={dt.searchInput}
                onChange={(e) => dt.setSearchInput(e.target.value)}
                placeholder="Search class or section name…"
                className="pl-9"
              />
            </div>
          }
        />
      )}

      <Modal
        open={!!detailId}
        onClose={() => setDetailId(null)}
        title={detail?.name ?? 'Class'}
        description={detail ? `Medium: ${detail.mediumName}` : undefined}
        size="md"
      >
        {detailLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : detail ? (
          <div className="space-y-4">
            <div>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Sections
              </h4>
              <SectionManager classId={detail.id} sections={detail.sections} />
            </div>
            <div>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Subjects &amp; teachers
              </h4>
              {classSubjectsLoading ? (
                <p className="text-sm text-slate-500">Loading…</p>
              ) : (
                <ClassSubjectsTable rows={classSubjects ?? []} />
              )}
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal open={!!editingClass} onClose={() => setEditingClass(null)} title="Edit Class" size="sm">
        <div className="space-y-3">
          <Field label="Medium" required>
            <Select value={editForm.mediumId} onChange={(e) => setEditForm((f) => ({ ...f, mediumId: e.target.value }))}>
              <option value="">Select Medium</option>
              {(mediums ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Name" required>
            <Input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
          </Field>
        </div>
        {update.error && <p className="mt-2 text-xs text-red-600">{errMsg(update.error)}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button className="btn-secondary" onClick={() => setEditingClass(null)}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!editForm.name.trim() || !editForm.mediumId || update.isPending}
            onClick={() => update.mutate()}
          >
            {update.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deletingClass}
        onClose={() => setDeletingClass(null)}
        onConfirm={() => remove.mutate()}
        title="Delete class?"
        message={
          deleteClassError ??
          `This will permanently delete "${deletingClass?.name}". This cannot be undone.`
        }
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </div>
  );
}
